import type { ConversationView } from '@swchat/client-sdk';
import type { Project } from '@swchat/protocol';
import { Avatar, Badge, Button, IconButton, Tabs } from '../design';
import { useClient } from '../lib/hooks';
import { LazyCallButtons } from './lazy';

export type ConversationTab = 'chat' | 'threads' | 'files' | 'activity';

/** Tab của đầu hội thoại theo thứ tự thiết kế: Hội thoại · Luồng trao đổi (W2.2) · Tệp (W2.3) · Hoạt động (W2.4). */
const TABS: { id: ConversationTab; label: string; testId: string }[] = [
  { id: 'chat', label: 'Hội thoại', testId: 'tab-chat' },
  { id: 'threads', label: 'Luồng trao đổi', testId: 'tab-threads' },
  { id: 'files', label: 'Tệp', testId: 'tab-files' },
  { id: 'activity', label: 'Hoạt động', testId: 'tab-activity' },
];

/**
 * Đầu hội thoại: avatar, tên, dòng phụ (đang gõ / số thành viên), nhãn mã dự án (không tiến độ % — W3), nút xác minh
 * (1-1), thành viên (nhóm), bật/tắt khung thông tin.
 */
export function ConversationHeader({
  conversation,
  project,
  typing,
  contextOpen,
  tab,
  threadCount,
  onTab,
  onBack,
  onVerify,
  onMembers,
  onToggleContext,
  searchOpen,
  onToggleSearch,
}: {
  conversation: ConversationView;
  project?: Project;
  typing: string[];
  contextOpen: boolean;
  tab: ConversationTab;
  /** Số luồng (hiện cạnh nhãn tab). */
  threadCount: number;
  onTab: (tab: ConversationTab) => void;
  onBack: () => void;
  onVerify?: () => void;
  onMembers?: () => void;
  onToggleContext: () => void;
  /** Thanh tìm trong hội thoại đang mở. */
  searchOpen: boolean;
  onToggleSearch: () => void;
}) {
  const client = useClient();
  return (
    <>
      <header className="ws-chat-head">
        <IconButton className="ws-back" label="Quay lại" icon="back" data-testid="back-to-list" onClick={onBack} />
        <Avatar name={conversation.title} seed={conversation.id} size="lg" />
        <div className="ws-chat-title">
          <h2>
            <span data-testid="chat-title">{conversation.title}</span>
            {project && (
              <Badge tone="accent" title={project.name} testId="chat-project">
                #{project.code}
              </Badge>
            )}
          </h2>
          <div className="ws-chat-subtitle" data-testid="chat-subtitle">
            {typing.length
              ? `${typing.map((u) => client.getUser(u)?.display_name ?? '…').join(', ')} đang gõ…`
              : conversation.kind === 'direct'
                ? 'Chat 1-1 · mã hóa đầu cuối'
                : conversation.left
                  ? 'Bạn đã rời nhóm này'
                  : `${conversation.members.length} thành viên · mã hóa đầu cuối`}
          </div>
        </div>
        <div className="ws-chat-actions">
          <LazyCallButtons conversation={conversation} />
          {onVerify && (
            <Button size="sm" icon="shield" onClick={onVerify}>
              Xác minh
            </Button>
          )}
          {onMembers && (
            <Button size="sm" icon="people" data-testid="group-members" onClick={onMembers}>
              Thành viên
            </Button>
          )}
          <IconButton
            label="Tìm trong hội thoại"
            icon="search"
            active={searchOpen}
            aria-expanded={searchOpen}
            data-testid="conv-search-toggle"
            onClick={onToggleSearch}
          />
          <IconButton
            label={contextOpen ? 'Ẩn khung thông tin' : 'Hiện khung thông tin'}
            icon="panel"
            active={contextOpen}
            aria-expanded={contextOpen}
            data-testid="toggle-context"
            onClick={onToggleContext}
          />
        </div>
      </header>
      <div className="ws-chat-tabs">
        <Tabs
          label="Nội dung hội thoại"
          items={TABS.map((t) => (t.id === 'threads' ? { ...t, count: threadCount } : t))}
          value={tab}
          onChange={onTab}
        />
      </div>
    </>
  );
}
